import React, { useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useAppContext } from "@context/state";
import { MySchoolsCard, FloatingNav } from "@components/index";
import MemberResource from "@components/Main/Dashboard/MemberResource";
import SaiSection from "@components/Main/Dashboard/Home/SaiSection";
import BudgetSection from "@components/Main/Dashboard/Home/BudgetSection";
import { useAbility } from "@hooks/useAbility";
// Dynamically import DashboardWalkthrough to avoid SSR issues
const DashboardWalkthrough = dynamic(
  () => import("@components/Main/Dashboard/DashboardWalkthrough"),
  { ssr: false },
);

export async function getStaticProps() {
  return {
    props: {
      showPrimaryNav: true,
      showSecondaryNav: false,
      showHeader: true,
    },
  };
}

export default function Home() {
  const {
    schoolCardExpanded,
    setUpdateNav,
    user,
    setRefreshPageModal,
  } = useAppContext();

  const router = useRouter();
  const { isCollaborator } = useAbility();
  const isMarkedWalkthroughDashboard =
    user?.marked_walkthrough_dashboard === "true";
  // Onboarding is finished, so the dashboard tutorial can start right away
  const hasCompletedOnboarding =
    !!user?.account_created_with_new_onboard &&
    (!!user?.is_fast_track_completed ||
      !!user?.inviter?.is_fast_track_completed);

  // Handle onboarding redirect
  useEffect(() => {
    if (!user || Object.keys(user).length === 0) return;

    const shouldRedirectToOnboard =
      user.is_select_advanced_profile !== "true" &&
      user.is_fast_track_completed !== "true" &&
      user.account_created_with_new_onboard &&
      (isCollaborator
        ? user.collaborator_onboarding_required === "true" // only new collaborators
        : true); // main account: always redirect if not completed

    if (shouldRedirectToOnboard) {
      const step = user.fast_track_onboarding_step || null;

      router.push({
        pathname: "/discovery-onboard",
        query: step ? { step } : {}, // Only include step query if it exists
      });
    }
  }, [user]);

  // Set navigation update only once
  useEffect(() => {
    setUpdateNav(0);
  }, [setUpdateNav]);

  // Optimize router query effect with proper dependencies
  useEffect(() => {
    const { success } = router.query;

    if (success === "true") {
      setRefreshPageModal(true);

      // Optional: Remove the query param so the modal doesn't show again on refresh
      const cleanedUrl = router.pathname;
      router.replace(cleanedUrl, undefined, { shallow: true });
    }
  }, [router.query?.success, router.pathname, setRefreshPageModal, router]);

  // Memoize grid classes to prevent string concatenation on every render
  const gridClasses = useMemo(() => {
    return `grid grid-cols-1 gap-4 flex-1 min-h-0 ${
      schoolCardExpanded
        ? "lg:grid-cols-[250px_1fr_200px] xl:grid-cols-[300px_1fr_350px]"
        : "lg:grid-cols-[250px_1fr_100px] xl:grid-cols-[300px_1fr_120px]"
    }`;
  }, [schoolCardExpanded]);

  return (
    <div
      id="content"
      className="p-2 sm:p-4 lg:p-6 flex flex-col h-full overflow-hidden"
    >
      {hasCompletedOnboarding && !isMarkedWalkthroughDashboard && (
        <DashboardWalkthrough />
      )}

      {/* Mobile and Tablet Layout (< 1024px) */}
      <div className="lg:hidden flex-1 overflow-y-auto">
        <div className="space-y-4">
          <div id="sai-values" className="space-y-6">
            <SaiSection />
            <BudgetSection />
          </div>

          <div id="my-schools-card" className="w-full">
            <MySchoolsCard />
          </div>

          <MemberResource />
        </div>
      </div>

      {/* Desktop Layout (>= 1024px) */}
      <div className={`hidden lg:grid ${gridClasses} overflow-hidden`}>
        {/* Left Column: MemberResource */}
        <div className="col-span-1 min-h-0 overflow-y-auto">
          <MemberResource />
        </div>

        {/* Middle Column: SAI and budget */}
        <div className="col-span-1 flex flex-col min-h-0 overflow-y-auto pb-24">
          <div id="sai-values" className="space-y-6">
            <SaiSection />
            <BudgetSection />
          </div>
        </div>

        {/* Right Column: MySchoolsCard */}
        <div
          id="my-schools-card"
          className="col-span-1 min-h-0"
        >
          <MySchoolsCard />
        </div>
      </div>
      <FloatingNav />
    </div>
  );
}
